/**
 * Rating Utilities
 * Shared functions for converting between emoji and numeric ratings
 */

// Emoji to numeric rating mapping
const emojiRatingMap = {
  '😞': 1,
  '😑': 2,
  '🙂': 3,
  '😄': 4,
  '🤩': 5,
};

// Numeric rating to emoji mapping (reverse)
const ratingEmojiMap = {
  5: '🤩',
  4: '😄',
  3: '🙂',
  2: '😑',
  1: '😞',
};

/**
 * Convert emoji to numeric rating (1-5)
 * @param {string} emoji - The emoji character
 * @returns {number|null} - Rating 1-5 or null if not found
 */
export const emojiToRating = (emoji) => {
  if (!emoji) return null;
  return emojiRatingMap[emoji] || null;
};

/**
 * Convert numeric rating to emoji
 * @param {number} rating - Rating 1-5
 * @returns {string|null} - Emoji character or null if invalid
 */
export const ratingToEmoji = (rating) => {
  const numRating = Math.round(Number(rating));
  if (numRating < 1 || numRating > 5) return null;
  return ratingEmojiMap[numRating] || null;
};

/**
 * Get numeric rating from feedback data
 * Handles multiple formats: numeric rating, emoji, score, value
 * @param {object|number|string} feedback - Feedback data
 * @returns {number} - Numeric rating 1-5, or 0 if invalid
 */
export const getFeedbackRating = (feedback) => {
  if (!feedback) return 0;

  // If it's already a number
  if (typeof feedback === 'number') {
    return feedback >= 1 && feedback <= 5 ? feedback : 0;
  }

  // If it's an emoji string
  if (typeof feedback === 'string' && emojiRatingMap[feedback]) {
    return emojiRatingMap[feedback];
  }

  // If it's an object with various possible properties
  if (typeof feedback === 'object') {
    // Check for direct rating
    if (feedback.rating && feedback.rating >= 1 && feedback.rating <= 5) {
      return feedback.rating;
    }
    // Check for score
    if (feedback.score && feedback.score >= 1 && feedback.score <= 5) {
      return feedback.score;
    }
    // Check for value
    if (feedback.value && feedback.value >= 1 && feedback.value <= 5) {
      return feedback.value;
    }
    // Check for emoji and convert
    if (feedback.emoji) {
      return emojiRatingMap[feedback.emoji] || 0;
    }
  }

  return 0;
};

/**
 * Calculate average rating from an array of feedback/ratings
 * @param {Array} feedbackArray - Array of feedback objects or ratings
 * @returns {number} - Average rating rounded to 1 decimal place
 */
export const calculateAverageRating = (feedbackArray) => {
  if (!Array.isArray(feedbackArray) || feedbackArray.length === 0) {
    return 0;
  }

  const validRatings = feedbackArray
    .map(getFeedbackRating)
    .filter(r => r > 0);

  if (validRatings.length === 0) return 0;

  const sum = validRatings.reduce((acc, rating) => acc + rating, 0);
  return Math.round((sum / validRatings.length) * 10) / 10;
};

/**
 * Get rating label from numeric rating
 * @param {number} rating - Rating 1-5
 * @returns {string} - Label text
 */
export const getRatingLabel = (rating) => {
  const labels = {
    5: 'Loved it!',
    4: 'Great',
    3: 'Okay',
    2: 'Not great',
    1: 'Poor',
  };
  return labels[Math.round(rating)] || 'Unknown';
};

export default {
  emojiToRating,
  ratingToEmoji,
  getFeedbackRating,
  calculateAverageRating,
  getRatingLabel,
};
